import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const runDemandForecastStep = defineStepConfig({
  type: "run_demand_forecast",
  name: "Run Demand Forecast",
  description:
    "Aggregates outbound demand history (sales-out + transfers-out) per SKU×warehouse, forecasts the next horizon of months (Holt-Winters / moving-average), derives safety stock + reorder point, and persists the forward projection (im_demand_forecasts) and policy outputs.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    divisionId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Scope the run to one division. Omit to forecast every division.",
      ),
    dryRun: z
      .boolean()
      .optional()
      .describe(
        "If true, forecast but do not persist forecasts or policy outputs. Defaults to false.",
      ),
  }),
  // Only the run id is exposed downstream — it's the handle to query this run's
  // rows in im_demand_forecasts. Per-run counts are logged, not surfaced.
  outputSchema: z.object({
    forecastRunId: z.string().uuid(),
  }),
  configSchema: z.object({
    historyMonths: z
      .number()
      .int()
      .min(12)
      .max(60)
      .prefault(24)
      .describe(
        "Months of outbound history to read. ≥24 lets Holt-Winters fit ≥2 seasonal cycles.",
      ),
    horizonMonths: z
      .number()
      .int()
      .min(1)
      .max(24)
      .prefault(6)
      .describe("Number of future months to project per series."),
  }),
  emits: [] as const,
  execution: {
    // Per-series forecasting is CPU-bound across all divisions — give it room.
    startToCloseTimeout: "60 minutes",
    // Persistence is insert-only (forecast rows are versioned by forecastRunId),
    // so a retry would re-run from scratch under a NEW run id and double-insert
    // the rows from already-committed divisions. Disable auto-retry to keep runs
    // clean; a failed nightly simply waits for the next schedule.
    retry: { maximumAttempts: 1 },
  },
});
